// MCP Trust Registry lookup — raw fetch (the endpoint is manually mounted,
// like /devices and /login, not in openapi). Resolves a discovered MCP server
// name to its cached registry evaluation: verdict, findings, and tools.
import { useCallback } from "react";
import { useAuth } from "@/lib/auth/AuthProvider";

const BASE_URL = import.meta.env.BASE_URL + "api/v1";

export interface RegistryFinding {
  rule_id: string;
  rule_name: string;
  severity: string; // LOW | MEDIUM | HIGH | CRITICAL
  confidence: string; // low | medium | high
  cwes?: string[];
  finding: string; // SECURE | VULNERABLE | NOT_APPLICABLE
  rationale?: string;
  remediation?: string[];
}

export interface RegistryTool {
  name: string;
  description: string;
  status?: string;
  type?: string;
}

export interface RegistryEvaluation {
  lookup_name: string;
  in_registry: boolean;
  server_name?: string;
  repo_path?: string;
  repo_url?: string;
  stars?: number;
  language?: string;
  risk?: string;
  org_type?: string;
  risk_score?: number;
  resolved_version?: string;
  commit_hash?: string;
  num_tools?: number;
  num_findings?: number;
  size_gb?: number;
  transports?: string[];
  risk_assessment?: string;
  top_remediations?: string;
  findings?: RegistryFinding[];
  tools?: RegistryTool[];
  synced_at?: string;
}

export interface RegistryLookup {
  name: string;
  configured: boolean;
  in_registry: boolean;
  match_confidence?: "name" | "low" | "";
  transport_mismatch?: boolean;
  evaluation?: RegistryEvaluation;
}

export interface RegistrySearch {
  q: string;
  configured: boolean;
  count: number;
  results: RegistryEvaluation[];
}

export function useMcpRegistryApi() {
  const { token, onUnauthorized } = useAuth();

  // Multi-result search over the registry (matches the PaaS "Search Registry").
  const search = useCallback(
    async (q: string): Promise<RegistrySearch> => {
      const headers = new Headers();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      const res = await fetch(`${BASE_URL}/mcp-registry/search?${new URLSearchParams({ q }).toString()}`, { headers });
      if (res.status === 401) onUnauthorized();
      if (!res.ok) throw new Error(`registry search failed (${res.status})`);
      return (await res.json()) as RegistrySearch;
    },
    [token, onUnauthorized],
  );

  const lookup = useCallback(
    async (name: string, transport?: string): Promise<RegistryLookup> => {
      const headers = new Headers();
      if (token) headers.set("Authorization", `Bearer ${token}`);
      const q = new URLSearchParams({ name });
      if (transport) q.set("transport", transport);
      const res = await fetch(`${BASE_URL}/mcp-registry?${q.toString()}`, {
        headers,
      });
      if (res.status === 401) onUnauthorized();
      if (!res.ok) throw new Error(`registry lookup failed (${res.status})`);
      return (await res.json()) as RegistryLookup;
    },
    [token, onUnauthorized],
  );

  return { lookup, search };
}
